import React, {useState} from "react";
import {Box} from "@mui/material";
import NavBar from "./components/NavBar";
import AuthDialog from "./components/AuthDialog";
import Landing from "./pages/Landing";
import Documents from "./pages/Documents";
import Document from "./pages/Document";
import {PublishedDocument} from "./util/api";
import {Invitation} from "./pages/ForgotPassword";
import {clearSession, isSignedIn} from "./util/SessionContext";

// The Customer portal. One page under a title bar; signing in happens in a dialog over it, which
// opens by itself for a signed-out visitor.
//
// No router, still. The authentication steps are stages of a dialog rather than places you can be,
// and a URL per step would only invite somebody to deep-link into the middle of a password reset.
//
// AND NO URL PER DOCUMENT EITHER, which is a choice rather than an omission. A deep link to a
// document is a link somebody will send to a colleague who was never granted it -- they would
// land on a 404 that looks like a broken system rather than on the sign-in they actually need.
// Everything here is reached from the list, which is reached by signing in. If customers ask to
// bookmark a document, that is the moment to add routing and a sign-in redirect together.

// An invitation arrives as ?invite=<key>.<code>, the two halves the reset endpoint already expects.
// It is read ONCE, at mount, and stripped from the address bar immediately: the key is single use,
// so leaving it in the URL only invites a refresh to replay a spent invitation and read as broken.
function readInvitation(): Invitation | undefined {
    const value = new URLSearchParams(window.location.search).get("invite");
    if (!value) return undefined;

    const separator = value.indexOf(".");
    if (separator <= 0 || separator === value.length - 1) return undefined;

    window.history.replaceState({}, "", window.location.pathname);

    return {key: value.slice(0, separator), code: value.slice(separator + 1)};
}

export default function App() {
    const [invitation] = useState<Invitation | undefined>(readInvitation);
    // Which document is open, or none. The whole of this portal's navigation.
    const [open, setOpen] = useState<PublishedDocument | null>(null);

    // An invitation names one person, who is not necessarily whoever this browser is already signed
    // in as -- a shared machine, or a second contact at the same company. Following the link
    // therefore ends the current session rather than being swallowed by it, which is also what stops
    // the link doing nothing at all for someone who happens to be signed in.
    const [signedIn, setSignedIn] = useState<boolean>(() => {
        if (invitation) {
            clearSession();
            return false;
        }
        return isSignedIn();
    });
    const [authOpen, setAuthOpen] = useState<boolean>(() => !!invitation || !isSignedIn());

    const signOut = () => {
        clearSession();
        setSignedIn(false);
        setAuthOpen(true);
        // NOTHING STAYS ON SCREEN THROUGH A SIGN-OUT. A document left rendered behind the dialog
        // is somebody else's SBOM on a shared machine.
        setOpen(null);
    };

    return (
        <Box sx={{height: "100%", display: "flex", flexDirection: "column"}}>
            <NavBar signedIn={signedIn} onSignIn={() => setAuthOpen(true)} onSignOut={signOut}/>

            <Box sx={{flex: 1, minHeight: 0, overflow: "auto"}}>
                {/* SIGNED OUT, THE LANDING PAGE STILL SAYS WHAT THIS SITE IS, for the visitor who
                    dismisses the sign-in dialog to read it. Signed in, it is replaced entirely:
                    a workspace that still says "your workspace is being set up" once there is
                    something in it would be the first thing a customer disbelieved. */}
                {!signedIn ? <Landing signedIn={false}/>
                    : open ? <Document summary={open} onBack={() => setOpen(null)}/>
                    : <Documents onOpen={setOpen}/>}
            </Box>

            <AuthDialog open={authOpen && !signedIn}
                        onClose={() => setAuthOpen(false)}
                        onSignedIn={() => { setSignedIn(true); setAuthOpen(false); }}
                        invitation={invitation}/>
        </Box>
    );
}
